/*
 * The search a scout last ran, so that opening a player is not a one-way trip.
 *
 * The complaint was specific: build a list, open someone from it, and the only
 * way back is the browser's Back button — which lands on an empty form with
 * every criterion gone. Ten fields, set by hand, thrown away by one click.
 *
 * Two things were on the table: a Back button on the player page, or a button
 * on the form that restores the last search. Neither is quite right on its own,
 * because both ask the scout to do something to get back to where they already
 * were. Going back should not be a feature you have to find.
 *
 * So: the criteria are kept here whenever a search runs, the form puts itself
 * back together on arrival, and the search re-runs on its own. Browser Back
 * works, the sidebar works, and the explicit button on the player page is there
 * for people who look for one — all three land on the same list.
 *
 * The rows themselves are deliberately NOT stored. Points move, players are
 * added, profiles are completed. Handing back a table from ten minutes ago that
 * quietly disagrees with the database is a worse failure than a short wait, so
 * the saved query is re-run and the answer is always current.
 *
 * sessionStorage, not localStorage: a half-built search belongs to the tab it
 * was built in, and should not surface in a new one days later.
 */

const KEY = 'listDraft'

export function saveListDraft(draft) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...draft, savedAt: Date.now() }))
  } catch (err) {
    /* Storage full or blocked. Losing the ability to go back is a nuisance;
       losing the search the scout just ran would be worse, so this stays
       silent and the page carries on. */
  }
}

export function loadListDraft() {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const draft = JSON.parse(raw)
    /* A draft with no query behind it cannot be re-run, so it is not a draft. */
    if (!draft || typeof draft.sql !== 'string') return null
    return draft
  } catch (err) {
    return null
  }
}

export function clearListDraft() {
  try {
    sessionStorage.removeItem(KEY)
  } catch (err) {
    /* Nothing to do — a stale draft is recoverable, a thrown error is not. */
  }
}

export function hasListDraft() {
  return loadListDraft() !== null
}
